/**
 * SettingsPage - User account and app settings
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Bell, Lock, Globe, Palette, LogOut, ChevronRight, 
  Moon, Volume2, Shield, HelpCircle, Mail, Smartphone,
  Eye, EyeOff, Check, X, ArrowLeft, Layout, Radio, List, Maximize2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Setting Item Component
const SettingItem = ({ icon: Icon, label, description, onClick, value, toggle }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors text-left"
  >
    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#00F0FF]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium">{label}</p>
      {description && <p className="text-[#71717A] text-sm truncate">{description}</p>}
    </div>
    {toggle !== undefined ? (
      <div className={`w-12 h-6 rounded-full p-1 transition-colors ${toggle ? 'bg-[#00F0FF]' : 'bg-white/10'}`}>
        <div className={`w-4 h-4 rounded-full bg-white transition-transform ${toggle ? 'translate-x-6' : ''}`} />
      </div>
    ) : value ? (
      <span className="text-[#71717A] text-sm">{value}</span>
    ) : (
      <ChevronRight className="w-5 h-5 text-[#71717A]" />
    )}
  </motion.button>
);

// Section Header
const SectionHeader = ({ title }) => (
  <h3 className="text-[#71717A] text-xs font-semibold uppercase tracking-wider px-4 mb-2 mt-6">
    {title}
  </h3>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const token = localStorage.getItem('token');
  
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const [privateProfile, setPrivateProfile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [playerLayout, setPlayerLayout] = useState('live_room');
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwState, setPwState] = useState({ loading: false, error: '', success: false });

  const submitPasswordChange = async () => {
    setPwState({ loading: true, error: '', success: false });
    if (pwForm.next.length < 6) {
      setPwState({ loading: false, error: 'New password must be at least 6 characters', success: false });
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwState({ loading: false, error: 'New passwords do not match', success: false });
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/change-password`,
        { currentPassword: pwForm.current, newPassword: pwForm.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwState({ loading: false, error: '', success: true });
      setPwForm({ current: '', next: '', confirm: '' });
      setTimeout(() => { setShowPasswordModal(false); setPwState({ loading: false, error: '', success: false }); }, 1500);
    } catch (err) {
      setPwState({ loading: false, error: err.response?.data?.detail || 'Failed to change password', success: false });
    }
  };

  // Fetch all user preferences (layout + toggles) from backend
  useEffect(() => {
    const fetchSettings = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/api/radio/settings/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const s = res.data || {};
        setPlayerLayout(s.player_layout || 'live_room');
        if (typeof s.notifications_enabled === 'boolean') setNotifications(s.notifications_enabled);
        if (typeof s.autoplay_enabled === 'boolean') setAutoPlay(s.autoplay_enabled);
        if (typeof s.private_profile === 'boolean') setPrivateProfile(s.private_profile);
      } catch (err) {
        console.error('Fetch settings error:', err);
      }
    };
    fetchSettings();
  }, [token]);

  // Persist a preference toggle to the backend, with optimistic UI + revert on failure
  const persistSetting = async (key, value, setter, prev) => {
    if (!token) return;
    setter(value);
    try {
      await axios.put(`${API_URL}/api/radio/settings`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Update setting error:', err);
      setter(prev); // revert on failure
    }
  };

  const updatePlayerLayout = async (layout) => {
    if (!token) return;
    try {
      await axios.put(`${API_URL}/api/radio/settings/player-layout`, 
        { layout },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayerLayout(layout);
      setShowLayoutPicker(false);
    } catch (err) {
      console.error('Update layout error:', err);
    }
  };

  const layoutOptions = [
    { id: 'live_room', name: 'Live Room', description: 'Full social experience with crowd visualizer and live chat', icon: Radio },
    { id: 'classic_player', name: 'Classic Player', description: 'Clean and minimal with collapsible chat drawer', icon: Layout },
    { id: 'feed_view', name: 'Feed View', description: 'Vertical scrolling cards with inline voting', icon: List },
    { id: 'focus_mode', name: 'Focus Mode', description: 'Distraction-free, just music and visuals', icon: Maximize2 },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-4 px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/5"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
            Settings
          </h1>
        </div>
      </div>

      <div className="px-4 py-2">
        {/* Account Section */}
        <SectionHeader title="Account" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={User}
            label="Edit Profile"
            description="Update your artist profile"
            onClick={() => navigate('/artist-setup')}
          />
          <SettingItem
            icon={Mail}
            label="Email"
            value={user?.email || 'Not set'}
            onClick={() => {}}
          />
          <SettingItem
            icon={Lock}
            label="Change Password"
            onClick={() => { setPwState({ loading: false, error: '', success: false }); setShowPasswordModal(true); }}
          />
        </div>

        {/* Notifications Section */}
        <SectionHeader title="Notifications" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={Bell}
            label="Push Notifications"
            description="Get notified about new activity"
            toggle={notifications}
            onClick={() => persistSetting('notifications_enabled', !notifications, setNotifications, notifications)}
          />
        </div>

        {/* Privacy Section */}
        <SectionHeader title="Privacy" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={Shield}
            label="Private Profile"
            description="Only followers can see your content"
            toggle={privateProfile}
            onClick={() => persistSetting('private_profile', !privateProfile, setPrivateProfile, privateProfile)}
          />
        </div>

        {/* Playback Section */}
        <SectionHeader title="Playback" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={Volume2}
            label="Auto-Play"
            description="Automatically play next track"
            toggle={autoPlay}
            onClick={() => persistSetting('autoplay_enabled', !autoPlay, setAutoPlay, autoPlay)}
          />
        </div>

        {/* Interface Section - Player Layout */}
        <SectionHeader title="Interface" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={Layout}
            label="Player Layout"
            description={layoutOptions.find(l => l.id === playerLayout)?.name || 'Live Room'}
            onClick={() => setShowLayoutPicker(true)}
          />
        </div>

        {/* Appearance Section */}
        <SectionHeader title="Appearance" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={Moon}
            label="Dark Mode"
            description="Always on for NLT Radio"
            toggle={darkMode}
            onClick={() => {}}
          />
        </div>

        {/* Support Section */}
        <SectionHeader title="Support" />
        <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
          <SettingItem
            icon={HelpCircle}
            label="Help Center"
            onClick={() => navigate('/help')}
          />
          <SettingItem
            icon={Mail}
            label="Contact Support"
            onClick={() => window.open('mailto:support@nltradio.com')}
          />
        </div>

        {/* Logout Button */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full mt-8 py-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 font-medium flex items-center justify-center gap-2 hover:bg-red-500/20 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </button>

        {/* App Version */}
        <p className="text-center text-[#52525B] text-sm mt-6">
          NLT Radio v1.0.0
        </p>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#12101A] rounded-2xl border border-white/10 p-6 max-w-sm w-full"
          >
            <h3 className="text-lg font-bold text-white mb-2">Sign Out?</h3>
            <p className="text-[#71717A] mb-6">Are you sure you want to sign out of your account?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-white/5 text-white font-medium hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="password-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#12101A] rounded-2xl border border-white/10 p-6 max-w-sm w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="p-1 rounded-full hover:bg-white/10">
                <X className="w-5 h-5 text-[#71717A]" />
              </button>
            </div>
            {pwState.success ? (
              <div className="text-center py-6" data-testid="password-success">
                <Check className="w-12 h-12 mx-auto mb-3 text-[#39FF14]" />
                <p className="text-white font-medium">Password updated</p>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  type="password"
                  placeholder="Current password"
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                  data-testid="current-password-input"
                />
                <input
                  type="password"
                  placeholder="New password"
                  value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                  data-testid="new-password-input"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                  data-testid="confirm-password-input"
                />
                {pwState.error && <p className="text-red-400 text-sm" data-testid="password-error">{pwState.error}</p>}
                <button
                  onClick={submitPasswordChange}
                  disabled={pwState.loading || !pwForm.current || !pwForm.next}
                  className="w-full py-3 rounded-xl bg-[#FF007F] text-white font-medium hover:bg-[#FF007F]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="submit-password-btn"
                >
                  {pwState.loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}


      {/* Player Layout Picker Modal */}
      {showLayoutPicker && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end sm:items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#12101A] rounded-t-3xl sm:rounded-2xl border border-white/10 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Player Layout</h3>
              <button
                onClick={() => setShowLayoutPicker(false)}
                className="p-2 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            <p className="text-[#71717A] text-sm mb-6">
              Choose how you want to experience the music player. Your preference will be saved.
            </p>

            <div className="space-y-3">
              {layoutOptions.map(option => {
                const IconComp = option.icon;
                const isSelected = playerLayout === option.id;
                
                return (
                  <button
                    key={option.id}
                    onClick={() => updatePlayerLayout(option.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      isSelected 
                        ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                        : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-[#00F0FF]/20' : 'bg-white/5'
                      }`}>
                        <IconComp className={`w-5 h-5 ${isSelected ? 'text-[#00F0FF]' : 'text-white'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{option.name}</span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-[#00F0FF]" />
                          )}
                        </div>
                        <p className="text-sm text-[#71717A] mt-1">{option.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-center text-[#52525B] text-xs mt-6">
              Changes apply immediately to your listening experience
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
